const API_BASE = process.env.REACT_APP_BACKEND_URL;

const TOKEN_KEY = 'admin_token';
const HTTP_UNAUTHORIZED = 401;
const DEFAULT_ANALYTICS_DAYS = 30;

async function fetchApi(endpoint, options = {}) {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const headers = { ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/api${endpoint}`, { ...options, headers, credentials: 'include' });
  if (res.status === HTTP_UNAUTHORIZED) {
    sessionStorage.removeItem(TOKEN_KEY);
    throw new Error('Unauthorized');
  }
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

// Public endpoints
export const getProjects = (category) => fetchApi(`/projects${category && category !== 'all' ? `?category=${category}` : ''}`);
export const getProject = (id) => fetchApi(`/projects/${id}`);
export const getServices = () => fetchApi('/services');
export const getTestimonials = () => fetchApi('/testimonials');
export const getCompanyInfo = () => fetchApi('/company-info');
export const getMaterials = () => fetchApi('/materials');

export const submitInquiry = (data) => fetchApi('/inquiries', {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
});

export const submitBooking = (data) => fetchApi('/bookings', {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
});

// Auth
export const login = async (email, password) => {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw new Error('Invalid credentials');
  const data = await res.json();
  if (data.token) sessionStorage.setItem(TOKEN_KEY, data.token);
  return data;
};

export const logout = () => { sessionStorage.removeItem(TOKEN_KEY); };
export const isLoggedIn = () => !!sessionStorage.getItem(TOKEN_KEY);

// Admin endpoints
export const getAdminStats = () => fetchApi('/admin/stats');
export const getInquiries = () => fetchApi('/inquiries');
export const getBookings = () => fetchApi('/bookings');

export const createProject = (data) => fetchApi('/admin/projects', {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
});
export const updateProject = (id, data) => fetchApi(`/admin/projects/${id}`, {
  method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
});
export const deleteProject = (id) => fetchApi(`/admin/projects/${id}`, { method: 'DELETE' });

export const updateService = (id, data) => fetchApi(`/admin/services/${id}`, {
  method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
});

export const updateInquiryStatus = (idx, status) => fetchApi(`/admin/inquiries/${idx}`, {
  method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
});
export const updateBookingStatus = (idx, status) => fetchApi(`/admin/bookings/${idx}`, {
  method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
});

// Blog endpoints (public)
export const getBlogs = (category) => fetchApi(`/blogs${category && category !== 'All' ? `?category=${category}` : ''}`);
export const getBlog = (slug) => fetchApi(`/blogs/${slug}`);

// Blog admin endpoints
export const createBlog = (data) => fetchApi('/admin/blogs', {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
});
export const updateBlog = (id, data) => fetchApi(`/admin/blogs/${id}`, {
  method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
});
export const deleteBlog = (id) => fetchApi(`/admin/blogs/${id}`, { method: 'DELETE' });

// WhatsApp
export const getWhatsAppSettings = () => fetchApi('/whatsapp/settings');
export const trackWhatsAppClick = (data) => fetchApi('/whatsapp/click', {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
});
export const updateWhatsAppSettings = (data) => fetchApi('/admin/whatsapp/settings', {
  method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
});
export const getWhatsAppClicks = () => fetchApi('/admin/whatsapp/clicks');

// Analytics
export const trackPageView = (data) => fetchApi('/track/pageview', {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
});
export const getAnalytics = (days = DEFAULT_ANALYTICS_DAYS) => fetchApi(`/admin/analytics?days=${days}`);

// Image Upload
export const uploadImage = async (file) => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_BASE}/api/upload/image`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
  return res.json();
};
